import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { TranslationService } from '../../shared/translation/translation.service';

export enum ToastMessageType {
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
  SUCCESS = 'success',
  COOKIE = 'cookie',
}

export interface ToastMessage {
  type: ToastMessageType;
  content: any;
  hasComponent: boolean;
  isExpirable: boolean;
  isClicked: boolean;
  callback: any;
  className?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastMessageService {
  private _translate = inject(TranslationService);
  private _document = inject(DOCUMENT);


  /**
   * Time before message deletes
   */
  private _expirationTime = 5 * 1000;

  /**
   * List of visible messages
   */
  public messagesList = signal<ToastMessage[]>([]);

  /**
   * Show ERROR toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   */
  public error(content, translateParams = null, hasComponent = false, isExpirable = true, className = '') {
    return this._showMessage(ToastMessageType.ERROR, content, translateParams, hasComponent, isExpirable, className);
  }

  /**
   * Show INFO toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   */
  public info(content, translateParams = null, hasComponent = false, isExpirable = true, className = '') {
    return this._showMessage(ToastMessageType.INFO, content, translateParams, hasComponent, isExpirable, className);
  }

  /**
   * Show WARNING toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   */
  public warning(content, translateParams = null, hasComponent = false, isExpirable = true, className = '') {
    return this._showMessage(ToastMessageType.WARNING, content, translateParams, hasComponent, isExpirable, className);
  }

  /**
   * Show SUCCESS toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   */
  public success(content, translateParams = null, hasComponent = false, isExpirable = true, className = '') {
    return this._showMessage(ToastMessageType.SUCCESS, content, translateParams, hasComponent, isExpirable, className);
  }

  public cookie(content, translateParams = null, hasComponent = false, isExpirable = true, className = '') {
    return this._showMessage(ToastMessageType.COOKIE, content, translateParams, hasComponent, isExpirable, className);
  }

  /**
   * Showing toast message
   *
   * @param type
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param className
   * @private
   */
  private _showMessage(type: ToastMessageType, content, translateParams, hasComponent, isExpirable, className?: string) {
    const message: ToastMessage = {
      type,
      content: this._setContent(content, translateParams),
      hasComponent,
      isExpirable,
      isClicked: false,
      className,
      callback: () => {}
    };

    this.messagesList.update(list => [...list, message]);

    /**
     * Removing created message after time expires
     */
    if (isExpirable) {
      setTimeout(() => {
        this.removeMessage(message);
      }, this._expirationTime);
    }

    return message;
  }

  /**
   * Sets content for toast message
   */
  private _setContent(content, translateParams) {
    if (typeof content === 'string') {
      return this._translate.translate(content, translateParams);
    } else {
      return content;
    }
  }

  public removeMessage(message) {
    message.callback();
    this.messagesList.update(list => list.filter(m => m !== message));
  }

  public hideCookieOneSignal() {
    const root = this._document.documentElement!;
    root.classList.add('no-onesignal');
    root.classList.add('no-cookie-message');
  }

  public showCookieOneSignal() {
    const root = this._document.documentElement!;
    root.classList.remove('no-onesignal');
    root.classList.remove('no-cookie-message');
  }
}
